/**
 * Additional Info Section
 * Transport Mode, Cargo Type, Container Type, Temperature Details (condizionale)
 */

import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Thermometer01 } from '@untitledui/icons';
import type { ContainerFormData, SelectOption } from '@/hooks/useContainerForm';
import { useTranslation } from 'react-i18next';

interface AdditionalInfoSectionProps {
    formData: ContainerFormData;
    onInputChange: (field: keyof ContainerFormData, value: string) => void;
    transportModeOptions: SelectOption[];
    cargoTypeOptions: SelectOption[];
    containerTypeOptions: SelectOption[];
    showTemperatureFields: boolean;
}

export function AdditionalInfoSection({
    formData,
    onInputChange,
    transportModeOptions,
    cargoTypeOptions,
    containerTypeOptions,
    showTemperatureFields,
}: AdditionalInfoSectionProps) {
    const { t } = useTranslation('pages:containers');

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.additionalInfo', 'Additional Info')}</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <Select
                    label={t('form.fields.transportMode', 'Transport Mode')}
                    items={transportModeOptions}
                    selectedKey={formData.transportMode}
                    onSelectionChange={(key) => onInputChange('transportMode', key as string)}
                    size="md"
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>

                <Select
                    label={t('form.fields.cargoType', 'Cargo Type')}
                    items={cargoTypeOptions}
                    selectedKey={formData.cargoType}
                    onSelectionChange={(key) => onInputChange('cargoType', key as string)}
                    size="md"
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>

                <Select
                    label={t('form.fields.containerType', 'Container Type')}
                    items={containerTypeOptions}
                    selectedKey={formData.containerType}
                    onSelectionChange={(key) => onInputChange('containerType', key as string)}
                    size="md"
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
            </div>

            {/* Temperature Details (shown for reefer) */}
            {showTemperatureFields && (
                <div className="mt-6 p-4 rounded-lg bg-secondary ring-1 ring-tertiary">
                    <div className="flex items-center gap-2 mb-4">
                        <Thermometer01 className="size-5 text-brand-600" />
                        <h3 className="font-medium text-primary">{t('form.sections.temperatureDetails', 'Temperature Details')}</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <Input
                            label={t('form.fields.temperature', 'Temperature (°C)')}
                            type="number"
                            placeholder="-18"
                            value={formData.temperatureSetpoint}
                            onChange={(value) => onInputChange('temperatureSetpoint', value)}
                            size="md"
                        />
                        <Input
                            label={t('form.fields.oxygen', 'Oxygen (%)')}
                            type="number"
                            placeholder="21"
                            value={formData.oxygenPercentage}
                            onChange={(value) => onInputChange('oxygenPercentage', value)}
                            size="md"
                        />
                        <Input
                            label={t('form.fields.co2', 'CO2 (%)')}
                            type="number"
                            placeholder="0"
                            value={formData.co2Percentage}
                            onChange={(value) => onInputChange('co2Percentage', value)}
                            size="md"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
